/**
 * Story contracts, ARCHITECTURE.md §4.6.
 *
 * Three shapes, one direction: the model emits `AuthoringOutline` (Pass A)
 * and `AuthoringScenes` (Pass B); the compiler produces the `StoryScript`
 * (stored, edited in the timeline); the baker produces the `Bundle` the
 * player runs. Units: `Pt` percent of the stage; `dur`, `hold`, `fade`,
 * `auto_after` seconds; `speed` percent-of-stage-width per second at
 * scale 1.0; `gain_db` decibels; `zoom` 1.0 = full stage.
 */
import { z } from 'zod';
import { Facing, Id, IsoDateTime, NonNegInt, Pct, Poly, Pt, Seconds } from './common';
import { Depth } from './stage';
import { AgeBandId } from './vocabulary';
import { NormalizedMarker } from './character';
import { EventIssue, StoryEvent } from './events';

// ---------------------------------------------------------------------------
// 4.6.2 What the model emits (flat, every field required, "none" sentinels)
// ---------------------------------------------------------------------------

export const AuthoringVerb = z.enum([
  'narrate',
  'say',
  'enter',
  'exit',
  'walk_to',
  'face',
  'play_clip',
  'emote',
  'pick_up',
  'put_down',
  'give',
  'hide',
  'appear',
  'camera',
  'wait',
  'sfx',
  'question',
]);
export type AuthoringVerb = z.infer<typeof AuthoringVerb>;

export const AuthoringOutline = z
  .strictObject({
    refused: z.boolean(),
    refusal_reason: z.string(),
    title: z.string(),
    /** Enum from vocab(theme), sorted. */
    theme: z.string(),
    refrain: z.string(),
    refrain_resolved: z.string(),
    cast: z.array(
      z.strictObject({
        character_id: z.string(),
        role: z.enum(['lead', 'friend', 'helper']),
        /** Echoed from the request; a quiet friend never gets `say`. */
        speaks: z.boolean(),
        wants: z.string(),
      }),
    ),
    /** Echoed from the request or ''; used only in `goodnight`. */
    listener_name: z.string(),
    /** Enum = bounded candidate props + 'none'. */
    props: z.array(z.string()),
    scenes: z.array(
      z.strictObject({
        beat: z.string(),
        stage_id: z.string(),
        time_of_day: z.string(),
        summary: z.string(),
        on_stage: z.array(z.string()),
        props_used: z.array(z.string()),
        music: z.string(),
      }),
    ),
  })
  .meta({ id: 'AuthoringOutline', title: 'Authoring outline', description: 'Pass A structured output.' });
export type AuthoringOutline = z.infer<typeof AuthoringOutline>;

/** EVERY field required; irrelevant ones are "none" / "" / 0. */
export const AuthoringRow = z.strictObject({
  verb: AuthoringVerb,
  /** cast id | 'narrator' | 'camera' | 'none' */
  actor: z.string(),
  /** anchor id | portal id | cast id | 'none' */
  target: z.string(),
  /** action key available on the actor | 'none' */
  clip: z.string(),
  /** Display names only; '' when not a speaking row; question rows: "question || bridge". */
  text: z.string(),
  emotion: z.string(),
  prop: z.string(),
  sfx: z.string(),
  /** '' or a label shared by rows that happen together. */
  group: z.string(),
  /** wait 0.3–3, camera 0.6–4, else 0 (ranges live in the validator). */
  secs: z.number(),
});
export type AuthoringRow = z.infer<typeof AuthoringRow>;

export const AuthoringScene = z.strictObject({
  scene_id: z.string(),
  stage_id: z.string(),
  time_of_day: z.string(),
  music: z.string(),
  ambient: z.string(),
  beat: z.string(),
  actions: z.array(AuthoringRow),
});
export type AuthoringScene = z.infer<typeof AuthoringScene>;

/** Pass B structured output — ALWAYS this envelope (length 1 per call for bands 5–8, all scenes for 3–4). */
export const AuthoringScenes = z
  .strictObject({ scenes: z.array(AuthoringScene) })
  .meta({ id: 'AuthoringScenes', title: 'Authoring scenes', description: 'Pass B structured output.' });
export type AuthoringScenes = z.infer<typeof AuthoringScenes>;

// ---------------------------------------------------------------------------
// 4.6.3 StoryScript
// ---------------------------------------------------------------------------

export const Word = z.strictObject({
  w: z.string(),
  /** Start / end, seconds. */
  s: Seconds,
  e: Seconds,
  /** Char index / length in the line text. */
  ci: NonNegInt,
  cl: NonNegInt,
});
export type Word = z.infer<typeof Word>;

export const AudioRef = z
  .strictObject({
    url: z.string().min(1),
    dur: Seconds,
    words: z.array(Word),
    timing: z.enum(['native', 'estimated']),
  })
  .meta({ id: 'AudioRef' });
export type AudioRef = z.infer<typeof AudioRef>;

const Audio = AudioRef.nullable();
const SideDir = z.enum(['left', 'right']);
const Handle = Id;

/** Portal or stage edge an actor enters from / exits via. */
export const Via = z.union([
  z.strictObject({ portal: Id }),
  z.strictObject({ edge: SideDir }),
]);
export type Via = z.infer<typeof Via>;

const Narrate = z.strictObject({ op: z.literal('narrate'), id: Id, text: z.string(), audio: Audio, dur: Seconds, hold: Seconds });
const Say = z.strictObject({
  op: z.literal('say'),
  id: Id,
  actor: Handle,
  text: z.string(),
  clip: z.string(),
  emotion: z.string().nullable(),
  bubble: z.enum(['speech', 'thought', 'none']),
  audio: Audio,
  dur: Seconds,
  hold: Seconds,
});
const Enter = z.strictObject({
  op: z.literal('enter'),
  id: Id,
  actor: Handle,
  from: Via,
  to: Pt,
  clip: z.string(),
  speed: z.number().min(0),
  path: z.array(Pt),
  dur: Seconds,
});
const Exit = z.strictObject({
  op: z.literal('exit'),
  id: Id,
  actor: Handle,
  via: Via,
  clip: z.string(),
  speed: z.number().min(0),
  path: z.array(Pt),
  dur: Seconds,
});
const WalkTo = z.strictObject({
  op: z.literal('walk_to'),
  id: Id,
  actor: Handle,
  to: Pt,
  anchor: z.string().nullable(),
  clip: z.string(),
  speed: z.number().min(0),
  path: z.array(Pt),
  dur: Seconds,
  /** `air`: the feet leave the ground for the trip (a leap, a flight): no footsteps, no step bob. */
  gait: z.enum(['air']).optional(),
});
const Face = z.strictObject({
  op: z.literal('face'),
  id: Id,
  actor: Handle,
  dir: z.union([SideDir, z.strictObject({ toward: Handle })]),
});
/**
 * `layer: 'upper'` (docs/SKEL-STORY.md Q6) plays `clip` as a masked pose OVER whatever the actor is
 * doing (walk, talk, idle): only the bones of `mask` (a `puppet.masks` name such as `upper_near`)
 * follow it, blended in and out over 200 ms. `then === clip` keeps the layer on after `dur` (a
 * `hold` pose) until the actor lets go of what it holds or another upper-layer clip replaces it.
 */
const PlayClip = z.strictObject({
  op: z.literal('play_clip'),
  id: Id,
  actor: Handle,
  clip: z.string(),
  loops: z.number().min(0),
  then: z.string(),
  dur: Seconds,
  layer: z.enum(['upper']).optional(),
  mask: z.string().optional(),
});
const Emote = z.strictObject({ op: z.literal('emote'), id: Id, actor: Handle, emotion: z.string(), dur: Seconds });
const PickUp = z.strictObject({ op: z.literal('pick_up'), id: Id, actor: Handle, prop: Handle, dur: Seconds });
const PutDown = z.strictObject({
  op: z.literal('put_down'),
  id: Id,
  actor: Handle,
  prop: Handle,
  at: Pt,
  anchor: z.string().nullable(),
  dur: Seconds,
});
const Give = z.strictObject({ op: z.literal('give'), id: Id, from: Handle, to: Handle, prop: Handle, dur: Seconds });
const Hide = z.strictObject({ op: z.literal('hide'), id: Id, actor: Handle, how: z.enum(['fade', 'pop']), dur: Seconds });
const Appear = z.strictObject({
  op: z.literal('appear'),
  id: Id,
  actor: Handle,
  at: Pt,
  anchor: z.string().nullable(),
  how: z.enum(['fade', 'pop']),
  clip: z.string(),
  dur: Seconds,
});
/**
 * What a camera move is for (SKEL-STORY §28). The bake-time director (`backend/app/story/camera.py`)
 * writes it; reduced motion reads it: a still shot becomes a cut, a `follow` / `action` / `end`
 * shot stays wide, a `push` leaves the cut as it is.
 */
export const CameraShot = z.enum(['wide', 'cast', 'single', 'two', 'close', 'follow', 'action', 'end', 'push']);
export type CameraShot = z.infer<typeof CameraShot>;

/**
 * How a bundle scene is entered (docs/SKEL-STORY.md "Scene transitions"). The bake-time director
 * (`backend/app/story/transitions.py`) writes it; the player plays it in story time. Seconds, like
 * every bundle duration: `lead` is the part of `dur` played before the scene's t = 0 (a pre-roll).
 * `fade` / `iris` are two-phase (out, `hold` fully covered, in); the rest are one-phase over a
 * snapshot of the outgoing picture. An old bundle without it plays a 0.8 s black fade (scene 0: a cut).
 */
export const TransitionKind = z.enum([
  'cut', 'fade', 'dissolve', 'iris', 'wipe', 'push', 'page_turn', 'zoom', 'clock_wipe', 'ripple',
]);
export type TransitionKind = z.infer<typeof TransitionKind>;
export const TransitionFocus = z.strictObject({
  /** Prefer this cast member's live head (survives seeks); `at` is the baked fallback. */
  actor: Handle.optional(),
  /** Stage %, x clamped to [12, 88], y to [10, 90] by the baker. */
  at: Pt,
  /** Radius of the circle at the covered end, % of the stage HEIGHT (default 12 ≈ a head). */
  r: Pct.optional(),
});
export type TransitionFocus = z.infer<typeof TransitionFocus>;
export const TransitionCard = z.strictObject({
  text: z.string().min(1).max(80),
  style: z.enum(['chapter', 'title', 'the_end']),
});
export type TransitionCard = z.infer<typeof TransitionCard>;
export const SceneTransition = z.strictObject({
  kind: TransitionKind,
  /** Seconds, the whole transition (story time: scales with speed, freezes on pause). */
  dur: Seconds.max(4),
  /** Seconds of `dur` played BEFORE this scene's t = 0 (pre-roll). 0 ≤ lead ≤ dur. `ending`: 0. */
  lead: Seconds.max(4),
  /** Seconds fully covered (two-phase kinds) / card shown over the old picture (one-phase kinds). */
  hold: Seconds.max(3).optional(),
  dir: z.enum(['left', 'right', 'up', 'down']).optional(),
  /** iris only. */
  shape: z.enum(['circle', 'star', 'heart']).optional(),
  /** The covered colour / the "from" fill when there is no old picture. Default black. */
  color: z.enum(['black', 'white', 'night', 'paper']).optional(),
  focus_out: TransitionFocus.optional(),
  focus_in: TransitionFocus.optional(),
  /** Little stars over the covered moment. */
  twinkle: z.boolean().optional(),
  card: TransitionCard.optional(),
  /** QA / tests only (like camera `serves`); the player ignores it. */
  why: z.array(z.string().max(120)).max(6).optional(),
});
export type SceneTransition = z.infer<typeof SceneTransition>;
/** Tints the whole scene (night falls on a day plate); it stays on under reduced motion. */
export const SceneGrade = z.enum(['dusk', 'night']);
export type SceneGrade = z.infer<typeof SceneGrade>;

const Camera = z.strictObject({
  op: z.literal('camera'),
  id: Id,
  target: z.union([Pt, z.strictObject({ actor: Handle })]),
  /** 1.0 = full stage; the director writes 1–1.8 (3 decimals), the player clamps to [1, 2]. */
  zoom: z.number().positive(),
  dur: Seconds,
  ease: z.enum(['in_out', 'linear']),
  shot: CameraShot.optional(),
  /** Cue ids the shot serves (QA and tests only; the player ignores it). */
  serves: z.array(Id).max(4).optional(),
});
const Wait = z.strictObject({ op: z.literal('wait'), id: Id, dur: Seconds });
const Sfx = z.strictObject({ op: z.literal('sfx'), id: Id, sound: z.string(), gain_db: z.number(), dur: Seconds.optional() });
const Music = z.strictObject({ op: z.literal('music'), id: Id, track: z.string().nullable(), gain_db: z.number(), fade: Seconds });
const Question = z.strictObject({
  op: z.literal('question'),
  id: Id,
  kind: z.enum(['completion', 'recall', 'open', 'wh', 'distancing']),
  prompt: z.string(),
  audio: Audio,
  bridge: z.string(),
  bridge_audio: Audio,
  dur: Seconds,
});
/**
 * scene_end: auto_after = null in tap mode, options.hold_auto_s / bedtime_hold_s otherwise (set by the player).
 * question:  auto_after = null in EVERY mode; the player may substitute options.question_auto_s (>= 6)
 *            only when the grown-up panel's "auto-answer" is on.
 */
const Checkpoint = z.strictObject({
  op: z.literal('checkpoint'),
  id: Id,
  kind: z.enum(['scene_end', 'question']),
  label: z.string(),
  auto_after: Seconds.nullable(),
});

// ---- Part 2 (docs/SKEL-STORY.md Q3): props in hands, tosses, rolls, effects, riding -------------

/**
 * Where a held prop sits on its holder (Q4). `top` is the bounds top-centre of props/mounts;
 * `other_hand` is a person's second hand (people hold two things, one per hand).
 */
export const SocketName = z.enum(['hand', 'other_hand', 'mouth', 'head', 'back', 'top']);
export type SocketName = z.infer<typeof SocketName>;

/** Q8: the procedural effects the player draws (no assets). */
export const FxKind = z.enum([
  'sparkle', 'hearts', 'stars', 'dust', 'splash', 'bubbles', 'confetti', 'zzz', 'notes', 'question', 'exclaim', 'crumbs', 'wind',
  // free stories (`backend/app/story/motion.py`): magic, the sky's weather, light
  'magic', 'rainbow', 'snow', 'rain', 'leaves', 'petals', 'fireflies', 'twinkle', 'shine', 'glow', 'puff', 'ripple', 'shooting_star',
  // words shown in the air: the letters or the words a line says (Alif, Ba, Ta -> ا ب ت), in `text`
  'words',
  // water dripping off a swimmer out of the water until dried (`backend/app/story/wet_fx.py`)
  'drips',
]);
export type FxKind = z.infer<typeof FxKind>;

/** An actor or prop handle, or a stage point in percent. */
export const StageTarget = z.union([Handle, Pt]).meta({ id: 'StageTarget', description: 'Actor/prop handle or [x, y] stage percent.' });
export type StageTarget = z.infer<typeof StageTarget>;

/** `prop` goes to `actor`'s `socket`, easing there from wherever it was over `blend_ms` (default 150). */
const Attach = z.strictObject({
  op: z.literal('attach'),
  id: Id,
  actor: Handle,
  prop: Handle,
  socket: SocketName,
  blend_ms: z.number().min(0).optional(),
  dur: Seconds.optional(),
});
/** `prop` leaves whoever holds it for the stage point `at` (its feet); `fall` drops it with gravity. */
const Detach = z.strictObject({
  op: z.literal('detach'),
  id: Id,
  prop: Handle,
  at: Pt,
  fall: z.boolean(),
  dur: Seconds.optional(),
});
/**
 * A parabola from `from` to `to` over `dur`. `apex` 0..4 = arc height (above 1 only for a toss into
 * one's own hands, which goes up 1.5 body heights): the peak rises
 * `apex × max(chord, 15)` stage-height percent above the chord's midpoint (chord in aspect-corrected
 * percent). `then`: `catch` = attach to the `to` actor's hand (its `socket`: `other_hand` for a
 * juggler's cross-hand catch, `head` for a thing landing on the head), `land` = rest at `to`,
 * `vanish` = hide.
 */
const Toss = z.strictObject({
  op: z.literal('toss'),
  id: Id,
  prop: Handle,
  from: StageTarget,
  to: StageTarget,
  apex: z.number().min(0).max(4),
  dur: Seconds,
  then: z.enum(['land', 'catch', 'vanish']),
  socket: SocketName.optional(),
});
/** Push / kick / roll along `path` (feet points). `spin`: turns (signed) or `true` = roll by distance. */
const PropMove = z.strictObject({
  op: z.literal('prop_move'),
  id: Id,
  prop: Handle,
  path: z.array(Pt),
  dur: Seconds,
  spin: z.union([z.boolean(), z.number()]).optional(),
});
/** A procedural particle effect at a handle or point; never blocks the clock. */
const Fx = z.strictObject({
  op: z.literal('fx'),
  id: Id,
  kind: FxKind,
  at: StageTarget,
  dur: Seconds,
  scale: z.number().positive().optional(),
  /** `words`: what is written in the air (any script: Arabic letters, a word, A B C). */
  text: z.string().min(1).max(80).optional(),
  /** A scene effect that keeps showing (sunshine): how long the story waited for it. */
  lingers: Seconds.optional(),
});
/**
 * A free move (`backend/app/story/motion.py`, `player/motion.ts`) laid over whatever the body does:
 * keyframes `[u, dx, dy, rot, sx, sy, a]` — `u` 0..1 of `dur`; `dx`/`dy` in BODY HEIGHTS of the mover
 * (`dx` forward, `dy` down: negative is up); `rot` degrees (positive tilts forward); `sx`/`sy` scale
 * (a negative `sx` is the body seen from the other side); `a` opacity. `pivot`: turn about the feet
 * or the body's middle. `keep`: the last key stays on the body afterwards; `reset`: the body eases
 * back to its own pose. `shake`: `[amplitude in body heights, hertz, degrees]`. `actor` is a cast
 * or a prop handle.
 */
const Motion = z.strictObject({
  op: z.literal('motion'),
  id: Id,
  actor: Handle,
  style: z.string(),
  dur: Seconds,
  keys: z.array(z.array(z.number()).length(7)).min(2).max(120),
  pivot: z.enum(['feet', 'center']).optional(),
  keep: z.boolean().optional(),
  reset: z.boolean().optional(),
  shake: z.tuple([z.number(), z.number(), z.number()]).optional(),
});
/** `actor` rides `on` (a prop or another actor): its seat goes to `on`'s `back` socket and follows it. */
const Mount = z.strictObject({ op: z.literal('mount'), id: Id, actor: Handle, on: Handle, dur: Seconds.optional() });
/** `actor` hops off whatever it rides to the stage point `at`. */
const Dismount = z.strictObject({ op: z.literal('dismount'), id: Id, actor: Handle, at: Pt, dur: Seconds.optional() });

/** Actions allowed inside a `par`: everything except checkpoint, question and par itself. */
export const ParChild = z.discriminatedUnion('op', [
  Narrate, Say, Enter, Exit, WalkTo, Face, PlayClip, Emote, PickUp, PutDown, Give, Hide, Appear, Camera, Wait, Sfx, Music,
  Attach, Detach, Toss, PropMove, Fx, Mount, Dismount, Motion,
]);
export type ParChild = z.infer<typeof ParChild>;

/** Children run together; the par ends at max(child.t + child.dur). Nests one level only. */
const Par = z.strictObject({ op: z.literal('par'), id: Id, actions: z.array(ParChild) });

export const Action = z.discriminatedUnion('op', [
  Narrate, Say, Enter, Exit, WalkTo, Face, PlayClip, Emote, PickUp, PutDown, Give, Hide, Appear, Camera, Wait, Sfx, Music,
  Attach, Detach, Toss, PropMove, Fx, Mount, Dismount, Motion,
  Question, Checkpoint, Par,
]).meta({ id: 'Action' });
export type Action = z.infer<typeof Action>;

export const Pronoun = z.enum(['she', 'he', 'they']);
export type Pronoun = z.infer<typeof Pronoun>;

// ---------------------------------------------------------------------------
// Puppets (docs/SKEL-STORY.md §3.5): every actor is a 2D-bone puppet
// ---------------------------------------------------------------------------

/** Which way a puppet view looks as authored. `camera` views are never flipped by facing. */
export const PuppetFacing = z.enum(['camera', 'right', 'left']);
export type PuppetFacing = z.infer<typeof PuppetFacing>;

/**
 * One way to play a story action: animation `anim` (its name) at `index` in the view's pruned
 * `root.animations`. `puppet.actions[action]` lists them in preference order (§3.2).
 */
export const PuppetActionRef = z
  .looseObject({
    view: Id,
    anim: z.string(),
    index: z.int().min(-1),
    loop: z.boolean(),
    fps: z.number().positive(),
    frames: z.number().min(0),
    dur_s: Seconds,
  })
  .meta({ id: 'PuppetActionRef' });
export type PuppetActionRef = z.infer<typeof PuppetActionRef>;

/** Rest-pose quad bounds of a view, world px, y up. `y0` is where the puppet meets the floor. */
export const PuppetBounds = z.strictObject({ x0: z.number(), y0: z.number(), x1: z.number(), y1: z.number() });
export type PuppetBounds = z.infer<typeof PuppetBounds>;

/**
 * A SkelForm 0.8 `armature.json` root (`frontend/src/skel/types.ts#SkfRoot`), checked only
 * structurally here: the skel runtime owns its shape.
 */
export const SkfRootShape = z.looseObject({
  version: z.string(),
  bones: z.array(z.looseObject({ id: z.int(), name: z.string(), parent_id: z.int() })),
  visuals: z.array(z.looseObject({ tex: z.string() })),
  styles: z.array(z.looseObject({ id: z.int(), name: z.string(), textures: z.array(z.looseObject({ name: z.string() })) })),
  animations: z.array(z.looseObject({ name: z.string(), fps: z.number(), keyframes: z.array(z.looseObject({})) })).optional(),
});

/** Q4: a socket = a bone plus an offset in that bone's own frame (world px, y up). */
export const PuppetSocket = z
  .looseObject({ bone: z.string(), offset: z.tuple([z.number(), z.number()]).optional() })
  .meta({ id: 'PuppetSocket' });
export type PuppetSocket = z.infer<typeof PuppetSocket>;

/**
 * `puppet.sockets` is keyed per view (`{view: {hand: {bone, offset}}}`); a flat `{hand: {…}}` (one
 * table for every view) is accepted too, and so is `views[v].sockets`. Same for `masks`
 * (`{view: {upper_near: [bone names]}}`). The player resolves all three.
 */
const PuppetSocketTable = z.record(z.string(), z.union([PuppetSocket, z.record(z.string(), PuppetSocket)]));
const PuppetMaskTable = z.record(z.string(), z.union([z.array(z.string()), z.record(z.string(), z.array(z.string()))]));

export const BundlePuppetView = z
  .looseObject({
    /** Body plan of the view (`front`, `side`, `quadruped`, `bird`, … `prop`, `custom`). */
    kind: z.string(),
    facing: PuppetFacing,
    /** Pruned root: bones, visuals, styles, IK, only the animations `actions` use. */
    root: SkfRootShape,
    /** `"<styleId>/<texName>"` → id in `Bundle.assets` (kind `texture`). */
    textures: z.record(z.string(), Id),
    bounds: PuppetBounds,
    /** Rest-pose body height in world px — the §8.2 divisor. */
    height_px: z.number().positive(),
    active_styles: z.array(z.int()).nullable().optional(),
    sockets: z.record(z.string(), PuppetSocket).optional(),
    masks: z.record(z.string(), z.array(z.string())).optional(),
  })
  .meta({ id: 'BundlePuppetView' });
export type BundlePuppetView = z.infer<typeof BundlePuppetView>;

export const BundlePuppet = z
  .looseObject({
    project_id: Id,
    rev: NonNegInt,
    default_view: Id,
    views: z.record(z.string(), BundlePuppetView),
    /** Story action → the ways to play it, preference-ordered (first whose view exists wins). */
    actions: z.record(z.string(), z.array(PuppetActionRef)),
    /** Q4 socket bones per view (see `PuppetSocketTable`). */
    sockets: PuppetSocketTable.optional(),
    /** Q6 upper-body layer masks per view: mask name → bone names. */
    masks: PuppetMaskTable.optional(),
  })
  .meta({ id: 'BundlePuppet' });
export type BundlePuppet = z.infer<typeof BundlePuppet>;

/** A story cast member (the compiled script's row). Extra keys are carried, not rejected. */
export const CastEntry = z
  .looseObject({
    /** 'bear' — what actions reference. */
    handle: Handle,
    name: z.string(),
    /** `skel:<ULID>` — the puppet this actor is. */
    uid: Id,
    /** Story category: person, animal, bird, fish, creature, object. */
    kind: z.string(),
    /** False = quiet friend (band cap); the compiler refuses `say` for it. */
    speaks: z.boolean(),
    /** Kokoro voice id (+ blends). */
    voice: z.string(),
    height_cm: z.number().positive(),
    pronoun: Pronoun.optional(),
  })
  .meta({ id: 'CastEntry' });
export type CastEntry = z.infer<typeof CastEntry>;

export const PropEntry = z
  .strictObject({
    handle: Handle,
    asset_uid: Id,
    display_name: z.string(),
    rest_surface: z.string(),
    rest_point: Pt,
    grip_point: Pt.nullable(),
    size_cm: z.number().positive(),
    scale_policy: z.enum(['world', 'fixed']),
    annotation_rev: NonNegInt,
  })
  .meta({ id: 'PropEntry' });
export type PropEntry = z.infer<typeof PropEntry>;

/** Q7 `drift`: slow travel across the stage, wrapping at the edges (clouds, fish, birds). */
export const PropDrift = z.looseObject({ dx_per_s: z.number(), wrap: z.boolean().optional() }).meta({ id: 'PropDrift' });
export type PropDrift = z.infer<typeof PropDrift>;

/**
 * A puppet thing on stage (docs/SKEL-STORY.md Part 2, Q1/Q7): a plot `prop` the story uses, or a
 * piece of living `scenery`. `at` is its feet in stage percent; `layer` picks the scenery layer
 * (between plate and actors) or the actors layer (z-sorted by feet with the cast); `ambient` is the
 * story action it loops (`sway`, `drift`, `bob`…) at `phase` 0..1 of its cycle.
 */
export const StagePropEntry = z
  .looseObject({
    handle: Handle,
    uid: Id,
    name: z.string(),
    kind: z.string().optional(),
    role: z.enum(['prop', 'scenery']),
    height_cm: z.number().positive(),
    at: Pt.optional(),
    layer: z.enum(['scenery', 'actors']).optional(),
    z_bias: z.number().optional(),
    ambient: z.string().nullable().optional(),
    phase: z.number().min(0).max(1).optional(),
    drift: PropDrift.nullable().optional(),
    /** A thing spread on the ground (a towel, a rug): its height squash while it lies there. */
    lies: z.number().positive().max(1).optional(),
    /** Such a thing in a hand is bunched up: drawn this share of its size (a beach towel held to dry off). */
    held: z.number().positive().max(1).optional(),
  })
  .meta({ id: 'StagePropEntry' });
export type StagePropEntry = z.infer<typeof StagePropEntry>;

/** CAST only: where each hero starts the scene. */
export const SceneInitial = z.array(
  z.strictObject({
    handle: Handle,
    at: Pt,
    facing: SideDir,
    clip: z.string(),
    visible: z.boolean(),
    holding: z.string().optional(),
  }),
);
export type SceneInitial = z.infer<typeof SceneInitial>;

/** PROPS at scene start: resting at `at`, or already in someone's `attached_to` socket. */
export const SceneInitialProps = z.array(
  z.strictObject({
    handle: Handle,
    at: Pt,
    anchor: z.string().nullable().optional(),
    visible: z.boolean(),
    attached_to: z.strictObject({ actor: Handle, socket: SocketName }).nullable().optional(),
  }),
);
export type SceneInitialProps = z.infer<typeof SceneInitialProps>;

/** A longer story's part: the compiler's pad floor and trim ceiling for this scene (`backend/app/story/parts.py`). */
export const SceneTiming = z.strictObject({ min_s: Seconds, max_s: Seconds });
export type SceneTiming = z.infer<typeof SceneTiming>;

const withT = <T extends z.ZodObject>(action: T) => action.extend({ t: Seconds });

/** A baked action: every non-`par` action plus its absolute time `t` within the scene. */
export const Cue = z.discriminatedUnion('op', [
  withT(Narrate), withT(Say), withT(Enter), withT(Exit), withT(WalkTo), withT(Face), withT(PlayClip), withT(Emote),
  withT(PickUp), withT(PutDown), withT(Give), withT(Hide), withT(Appear), withT(Camera), withT(Wait), withT(Sfx),
  withT(Music), withT(Attach), withT(Detach), withT(Toss), withT(PropMove), withT(Fx), withT(Mount), withT(Dismount),
  withT(Motion), withT(Question), withT(Checkpoint),
]).meta({ id: 'Cue' });
export type Cue = z.infer<typeof Cue>;

/** Observations derived from the physical interpreter, not model claims. */
export const EventEvidence = z.record(z.string(), z.record(z.string(), z.json()));
export const CompiledEventData = z.strictObject({
  event: StoryEvent, actions: z.array(Cue), evidence: EventEvidence, issues: z.array(EventIssue),
});
export const EventTiming = z.strictObject({
  evidence: EventEvidence, timing_confidence: z.enum(['native', 'phrase']), issues: z.array(EventIssue),
});

export const Scene = z.strictObject({
  id: Id,
  /** `micro` for a one-part story; a longer story's part kind (`hello`, `wish`, `try_1`, …, `goodnight`). */
  beat: z.string(),
  /** A longer story's chapter title ("The Missing Blanket"). */
  title: z.string().optional(),
  /** A longer story's arc time phrase ("That evening"); the transition director reads it. */
  when: z.string().max(80).optional(),
  /** A longer story's part mood (`calm`, `sleepy`, …). */
  mood: z.string().max(24).optional(),
  timing: SceneTiming.optional(),
  stage_uid: Id,
  annotation_rev: NonNegInt,
  time_of_day: z.string(),
  /** Audio uids. */
  music: z.string().nullable(),
  ambient: z.string().nullable(),
  initial: SceneInitial,
  initial_props: SceneInitialProps,
  /** Sequential; `par` nests one level. */
  actions: z.array(Action),
  compiled_events: z.array(CompiledEventData).optional(),
});
export type Scene = z.infer<typeof Scene>;

export const Repair = z.strictObject({
  code: z.string(),
  action_id: z.string(),
  strategy: z.string(),
  note: z.string(),
});
export type Repair = z.infer<typeof Repair>;

export const StoryScript = z
  .strictObject({
    version: z.literal(1),
    /** ULID = stories.uid */
    id: Id,
    title: z.string(),
    lang: z.literal('en'),
    age_band: AgeBandId,
    theme: z.string(),
    refrain: z.string(),
    quality: z.enum(['standard', 'high']),
    /** Child's name used in `goodnight` (parent-entered, optional). */
    listener_name: z.string().nullable(),
    cast: z.array(CastEntry),
    /** Legacy annotated props, or Part 2 puppet props / scenery. */
    props: z.array(z.union([PropEntry, StagePropEntry])),
    scenes: z.array(Scene),
    audio: z.strictObject({ narrator_voice: z.string(), speed: z.number().positive(), music_gain_db: z.number() }),
    /** Band defaults (§4.6.1), overridable per story and by the grown-up panel. */
    options: z.strictObject({ questions: z.boolean(), subtitles: z.boolean() }),
    meta: z.strictObject({
      generated_at: IsoDateTime,
      model: z.string(),
      /** Actual accepted authors; retained across scoped chapter repairs. */
      outline_model: z.string().optional(),
      chapter_models: z.record(z.string(), z.string()).optional(),
      fallback_model: z.string().nullable(),
      cost_usd: z.number().min(0),
      validator_version: z.string(),
      vocab_rev: NonNegInt,
      /** asset_uid -> annotation rev validated against */
      pins: z.record(z.string(), NonNegInt),
      repairs: z.array(Repair),
      /** Seconds the story plays (estimated from word counts; the bundle has the measured ones). */
      runtime_s: z.number().min(0).optional(),
      /** A longer story's parts, in order (one scene each). */
      parts: z
        .array(
          z.strictObject({
            id: z.string(),
            kind: z.string(),
            title: z.string(),
            when: z.string().optional(),
            mood: z.string().optional(),
          }),
        )
        .optional(),
    }),
  })
  .meta({ id: 'StoryScript', title: 'Story script', description: 'Compiled, editable story (stories.script_json).' });
export type StoryScript = z.infer<typeof StoryScript>;

// ---------------------------------------------------------------------------
// 4.6.4 Bundle
// ---------------------------------------------------------------------------

/**
 * `texture` = one puppet part PNG; `object` = a static prop image; `pack` = many part textures
 * packed into one WebP page (`tex/pack-<key>-<n>.webp`, `backend/app/story/pack.py`).
 */
export const BundleAssetKind = z.enum(['plate', 'video', 'poster', 'texture', 'object', 'audio', 'font', 'pack']);
export type BundleAssetKind = z.infer<typeof BundleAssetKind>;

/** The baker may name an asset's location `src` (SKEL-STORY §3.5) or `url`; the player reads `url`. */
function srcToUrl(v: unknown): unknown {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
  const a = v as { url?: unknown; src?: unknown };
  if (typeof a.url === 'string' && a.url.length > 0) return v;
  if (typeof a.src !== 'string') return v;
  const { src, ...rest } = a as Record<string, unknown>;
  return { ...rest, url: src };
}

export const BundleAsset = z.preprocess(
  srcToUrl,
  z.strictObject({
    kind: BundleAssetKind,
    /** Relative to the directory that contains bundle.json (`tex/<sha>.png`), or root-absolute. */
    url: z.string().min(1),
    /**
     * The in-bundle path (§3.5 `tex/<sha>.png`). The baker writes it next to an absolute `url`
     * (the API route), and `export_zip` packs the file under it; the player reads `url`.
     */
    src: z.string().min(1).optional(),
    bytes: NonNegInt,
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    w: z.int().positive().optional(),
    h: z.int().positive().optional(),
    /**
     * A `texture` that also lives in a `pack` asset: its `w`×`h` pixels start at `x`,`y` of that
     * page. A player that knows packs fetches the page once instead of every part; one that does
     * not still loads `url` (the PNG stays in the bundle), so old players and old bundles both play.
     */
    pack: z
      .strictObject({ id: z.string().min(1), x: NonNegInt, y: NonNegInt })
      .optional(),
  }),
);
export type BundleAsset = z.infer<typeof BundleAsset>;

/**
 * `cast` / `props` as the baker writes them: an array of entries with `handle`, or an object
 * keyed by handle. Both become the keyed table the player (and this schema's output) uses.
 */
export function keyByHandle(v: unknown): unknown {
  if (!Array.isArray(v)) return v;
  const out: Record<string, unknown> = {};
  for (const row of v) {
    const h = row && typeof row === 'object' ? (row as { handle?: unknown }).handle : undefined;
    if (typeof h === 'string' && h.length > 0) out[h] = row;
  }
  return out;
}

/** Percent-of-frame body points, inherited from asset → clip → the selected frame. */
export const SpritePoints = z.record(z.string(), Pt);
export type SpritePoints = z.infer<typeof SpritePoints>;

export const BundleSpriteFrame = z.strictObject({
  asset: Id,
  x: NonNegInt,
  y: NonNegInt,
  w: z.int().positive(),
  h: z.int().positive(),
  duration_ms: z.number().positive(),
});
export type BundleSpriteFrame = z.infer<typeof BundleSpriteFrame>;

export const BundleSpriteClip = z.looseObject({
  action: z.string(),
  facing: z.string(),
  mirrored: z.boolean(),
  fps: z.number().positive(),
  loop: z.boolean(),
  points: SpritePoints,
  frame_points: z.record(z.string(), SpritePoints).optional(),
  frames: z.array(BundleSpriteFrame).min(1),
  source_id: z.string().min(1).optional(),
  semantic_markers: z.array(NormalizedMarker).optional(),
});
export type BundleSpriteClip = z.infer<typeof BundleSpriteClip>;

export const BundleSprite = z.looseObject({
  asset_uid: Id,
  rev: NonNegInt,
  default_clip: z.string().min(1),
  actions: z.record(z.string(), z.string()),
  points: SpritePoints,
  clips: z.record(z.string(), BundleSpriteClip),
}).refine((s) => s.clips[s.default_clip] !== undefined, { message: 'Default sprite clip must exist', path: ['default_clip'] })
  .meta({ id: 'BundleSprite' });
export type BundleSprite = z.infer<typeof BundleSprite>;

/** A baked cast member carries either its bone puppet or native sprite animation. */
export const BundleCastEntry = CastEntry.extend({
  render: z.enum(['skel', 'sprite']),
  puppet: BundlePuppet.optional(),
  sprite: BundleSprite.optional(),
}).refine((c) => c.render === 'sprite' ? !!c.sprite : !!c.puppet, {
  message: 'Cast render requires its corresponding puppet or sprite data',
}).meta({ id: 'BundleCastEntry' });
export type BundleCastEntry = z.infer<typeof BundleCastEntry>;

/**
 * A thing on stage as baked. Part 2 (`StagePropEntry` + its `puppet`): a puppet prop or scenery
 * piece looping its `ambient`. Legacy (`PropEntry` + `image`): one static image. One loose shape
 * carries both so the player reads either without a union; `props.ts` holds the accessors.
 */
export const BundlePropEntry = z
  .looseObject({
    handle: Handle,
    puppet: BundlePuppet.optional(),
    // Part 2
    uid: Id.optional(),
    name: z.string().optional(),
    kind: z.string().optional(),
    role: z.enum(['prop', 'scenery']).optional(),
    height_cm: z.number().positive().optional(),
    at: Pt.optional(),
    layer: z.enum(['scenery', 'actors']).optional(),
    z_bias: z.number().optional(),
    ambient: z.string().nullable().optional(),
    phase: z.number().min(0).max(1).optional(),
    drift: PropDrift.nullable().optional(),
    /** A thing spread on the ground (a towel, a rug): its height squash while it lies there. */
    lies: z.number().positive().max(1).optional(),
    /** Such a thing in a hand is bunched up: drawn this share of its size (a beach towel held to dry off). */
    held: z.number().positive().max(1).optional(),
    // legacy annotated prop
    asset_uid: Id.optional(),
    display_name: z.string().optional(),
    rest_surface: z.string().optional(),
    rest_point: Pt.optional(),
    grip_point: Pt.nullable().optional(),
    size_cm: z.number().positive().optional(),
    scale_policy: z.enum(['world', 'fixed']).optional(),
    annotation_rev: NonNegInt.optional(),
    image: Id.optional(),
  })
  .meta({ id: 'BundlePropEntry' });
export type BundlePropEntry = z.infer<typeof BundlePropEntry>;

export const BundlePlate = z.strictObject({
  asset: Id,
  video: Id.optional(),
  video_bytes: NonNegInt.optional(),
  poster: Id.optional(),
  depth: Depth,
  walkable: z.array(Poly),
  /** The plate's water: a thing resting on it floats (bobs and rocks, half under the surface). */
  water: z.array(Poly).optional(),
  blocks: z.array(Poly),
  occluders: z.array(
    z.strictObject({
      asset: Id.optional(),
      clip_region: Poly.optional(),
      baseline_y: Pct.optional(),
      parallax: z.number().min(0).max(2),
    }),
  ),
  anchors: z.record(z.string(), z.strictObject({ at: Pt, stand: Pt, facing: Facing })),
});
export type BundlePlate = z.infer<typeof BundlePlate>;

export const BundleScene = z.strictObject({
  id: Id,
  beat: z.string(),
  /** A longer story's chapter title (the player may show it at the part's "Next"). */
  title: z.string().optional(),
  plate: BundlePlate,
  music: z.string().nullable(),
  ambient: z.string().nullable(),
  initial: SceneInitial,
  initial_props: SceneInitialProps,
  /** Seconds, baked. */
  duration: Seconds,
  /** Flat, sorted by t; no `par`. */
  cues: z.array(Cue),
  event_timing: EventTiming.optional(),
  /** Asset ids needed before the scene may start. */
  preload: z.array(Id),
  /** Decoded bytes of this scene's textures (informational; the player measures its own). */
  texture_bytes: z.union([NonNegInt, z.record(z.string(), NonNegInt)]).optional(),
  /** How this scene is entered by play-through (scene 0: the story's opening). */
  transition: SceneTransition.optional(),
  /** Last scene only: plays after its scene_end hold is released, before the story ends. */
  ending: SceneTransition.optional(),
  /** Tint the whole scene (night falls on a day plate). */
  grade: SceneGrade.optional(),
});
export type BundleScene = z.infer<typeof BundleScene>;

export const BundleOptions = z.strictObject({
  hold_auto_s: z.literal(1.2),
  bedtime_hold_s: z.literal(2.0),
  /** Used only when the grown-up panel enabled auto-answer. */
  question_auto_s: z.literal(6),
  questions_default: z.boolean(),
  subtitles_default: z.boolean(),
  reduced_motion: z.literal('auto'),
  /** Subtitle floor in CSS px (§8.6). */
  min_text_px: z.literal(24),
  texture_budget_mib: z.strictObject({ phone: z.literal(128), tablet: z.literal(192), desktop: z.literal(256) }),
  /** Dim + release wake lock after this long at a hold. */
  idle_dim_s: z.literal(180),
});
export type BundleOptions = z.infer<typeof BundleOptions>;

/** Fixed player option values (§4.6.4). Only the two `*_default` flags vary per bundle. */
export const BUNDLE_OPTIONS_FIXED = {
  hold_auto_s: 1.2,
  bedtime_hold_s: 2.0,
  question_auto_s: 6,
  reduced_motion: 'auto',
  min_text_px: 24,
  texture_budget_mib: { phone: 128, tablet: 192, desktop: 256 },
  idle_dim_s: 180,
} as const satisfies Omit<BundleOptions, 'questions_default' | 'subtitles_default'>;

/**
 * A story still being made, published part by part (docs/STORY-STREAMING.md): the bundle holds the
 * first `parts_ready` scenes, each final, and grows by revisions. No `stream` key means complete —
 * every old bundle and every final one.
 */
export const BundleStream = z
  .strictObject({
    /** Grows with every publish; equal to `parts_ready`. */
    rev: z.number().int().min(1),
    /** Scenes in this revision (`scenes.length`). */
    parts_ready: z.number().int().min(1),
    parts_total: z.number().int().min(1),
    /** `paused`: the AI usage window was hit, it goes on at `resume_at`. `failed`: nothing more comes. */
    state: z.enum(['streaming', 'paused', 'failed']),
    /** ISO timestamp, set while `paused`. */
    resume_at: z.string().nullable(),
  })
  .meta({ id: 'BundleStream' });
export type BundleStream = z.infer<typeof BundleStream>;

export const Bundle = z
  .strictObject({
    version: z.literal(1),
    id: Id,
    title: z.string(),
    lang: z.string(),
    age_band: z.string(),
    stage: z.strictObject({ w: z.literal(1920), h: z.literal(1080) }),
    assets: z.record(z.string(), BundleAsset),
    /** Keyed by handle (an array of entries is accepted and keyed on parse). */
    cast: z.preprocess(keyByHandle, z.record(z.string(), BundleCastEntry)),
    props: z.preprocess(keyByHandle, z.record(z.string(), BundlePropEntry)),
    scenes: z.array(BundleScene),
    options: BundleOptions,
    /** Only on a partial bundle (a story still being made). */
    stream: BundleStream.optional(),
  })
  .meta({ id: 'Bundle', title: 'Story bundle', description: 'bundle.json — what the player loads.' });
export type Bundle = z.infer<typeof Bundle>;
